import { useNavigate } from 'react-router-dom';
import type { Startup, DashboardData } from '@shared/types';
import type { Pipeline } from '../../store/pipelineStore';
import { formatCurrency } from '../../utils/format';

interface Props {
  startup: Startup;
  metrics: DashboardData['metrics'];
  feedbackAnalytics: DashboardData['feedbackAnalytics'];
  pipeline: Pipeline | null;
  doneCount: number;
  npsColor: string;
  startupId?: string;
}

function MetricCard({
  label, value, sub, valueColor,
}: {
  label: string;
  value: string | number;
  sub?: string;
  valueColor?: string;
}) {
  return (
    <div className="card">
      <p className="text-gray-500 text-xs mb-1">{label}</p>
      <p className="text-2xl font-bold" style={{ color: valueColor ?? '#fff' }}>{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

export function StageMetrics({ startup, metrics, feedbackAnalytics, pipeline, doneCount, npsColor, startupId }: Props) {
  const navigate = useNavigate();
  const stage = startup.stage;

  if (stage === 'idea' || stage === 'validation') {
    const pipelineCompletedAt = pipeline ? [
      pipeline.idea?.completedAt,
      pipeline.realityCheck?.completedAt,
      pipeline.positioning?.completedAt,
      pipeline.validationPath?.completedAt,
      pipeline.paymentTest?.completedAt,
      pipeline.mvpBlueprint?.completedAt,
    ] : [];
    const pipelineDone = pipelineCompletedAt.filter(Boolean).length;
    const pipelineTotal = pipeline ? 6 : 0;

    const validationScore = pipeline?.realityCheck?.aiOutput
      ? (pipeline.realityCheck.aiOutput as Record<string, unknown>).validationScore as number | undefined
      : undefined;

    const vsColor = validationScore != null
      ? (validationScore >= 70 ? '#22c55e' : validationScore >= 40 ? '#f59e0b' : '#ef4444')
      : '#4b5563';

    const intelColor = doneCount >= 4 ? '#22c55e' : doneCount >= 2 ? '#f59e0b' : '#9ca3af';
    const pipelineColor = pipelineTotal > 0 && pipelineDone > 0 ? '#7c6ff7' : '#4b5563';

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-gray-500 text-xs mb-1">Validation Score</p>
          <p className="text-2xl font-bold" style={{ color: vsColor }}>
            {validationScore != null ? `${validationScore}/100` : '—'}
          </p>
          <p className="text-gray-500 text-xs mt-1">From reality check</p>
        </div>
        <div className="card">
          <p className="text-gray-500 text-xs mb-1">Intel Coverage</p>
          <p className="text-2xl font-bold" style={{ color: intelColor }}>{doneCount}/5</p>
          <p className="text-gray-500 text-xs mt-1">sections analyzed</p>
        </div>
        <div className="card">
          <p className="text-gray-500 text-xs mb-1">Pipeline Progress</p>
          <p className="text-2xl font-bold" style={{ color: pipelineColor }}>
            {pipelineTotal > 0 ? `${pipelineDone}/${pipelineTotal}` : '—'}
          </p>
          <p className="text-gray-500 text-xs mt-1">steps completed</p>
        </div>
        <div
          className="card"
          onClick={() => startupId && navigate(`/startups/${startupId}/financials`)}
          style={{ cursor: startupId ? 'pointer' : undefined, transition: 'border-color 0.15s' }}
          title={metrics.projectedARR > 0 ? undefined : 'Click to build your financial model'}
          onMouseEnter={e => startupId && (e.currentTarget.style.borderColor = 'rgba(249,115,22,0.4)')}
          onMouseLeave={e => startupId && (e.currentTarget.style.borderColor = '')}
        >
          <p className="text-gray-500 text-xs mb-1">Projected ARR</p>
          <p className="text-2xl font-bold" style={{ color: metrics.projectedARR > 0 ? '#22c55e' : '#4b5563' }}>
            {metrics.projectedARR > 0 ? formatCurrency(metrics.projectedARR, true) : '—'}
          </p>
          <p className="text-xs mt-1" style={{ color: metrics.projectedARR > 0 ? '#6b7280' : '#f97316' }}>
            {metrics.projectedARR > 0 ? '↑ Base case' : '→ Set up financials'}
          </p>
        </div>
      </div>
    );
  }

  // mvp and growth stages use the same financial metrics
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        label="Projected ARR"
        value={formatCurrency(metrics.projectedARR, true)}
        sub="↑ Base case"
        valueColor="#22c55e"
      />
      <MetricCard
        label="Break-even"
        value={metrics.breakEvenMonth ? `Month ${metrics.breakEvenMonth}` : 'N/A'}
        sub="Base scenario"
        valueColor="#7c6ff7"
      />
      <div className="card">
        <p className="text-gray-500 text-xs mb-1">Beta NPS</p>
        <p className="text-2xl font-bold" style={{ color: npsColor }}>
          {feedbackAnalytics.totalResponses > 0 ? `+${metrics.betaNPS}` : '—'}
        </p>
        <p className="text-gray-500 text-xs mt-1">{metrics.betaResponses} responses</p>
      </div>
      <MetricCard
        label="Runway"
        value={metrics.runwayMonths ? `${metrics.runwayMonths} mo` : '∞'}
        sub={metrics.burnRate ? `Burn: ${formatCurrency(metrics.burnRate, true)}/mo` : 'No burn rate'}
      />
    </div>
  );
}
